import { Button, Box, Typography } from '@evaneos/b2b-ui';

import { useIntl } from 'react-intl';

import { deprecated_CCFF90 } from '../../../../../styles/deprecated-colors';

import { useAppDispatch } from '../../../../../shared/redux';
import { AI as AiIcon } from '../../../../../design-system/Icons/AI';
import { useGenerateSummaryMail } from '../../../../../resources/phoning/mutations';
import { saveDraftMessage } from '../../../../../resources/discussion';
import { PhoneCall } from '../../../../../resources/phoning/types';
import { DossierId } from '../../../../../resources/dossier/types';
import { convertSpacesInHtml } from '../../utils/messageHtmlConverter';
import {
    useGetTrackedTripProperties,
    useTrackEventAmplitude,
} from '../../../../../shared/analytics';

const TEMP_AI_BUTTON_BG = deprecated_CCFF90;

type GenerateSummaryEmailBlockProps = {
    canGenerateEmailRecap: boolean;
    phoneCall: PhoneCall;
    dossierId: DossierId;
};

export const GenerateSummaryEmailBlock = ({
    dossierId,
    canGenerateEmailRecap,
    phoneCall,
}: GenerateSummaryEmailBlockProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const { mutate: generateSummaryMail, isPending: isGeneratingSummaryMail } =
        useGenerateSummaryMail(dossierId, {
            onSuccess: (data) => {
                dispatch(
                    saveDraftMessage(dossierId, data.content, convertSpacesInHtml(data.content), {
                        origin: 'phone_call_summary',
                        phoneCallId: phoneCall.id,
                        originalContent: data.content,
                    }),
                );
            },
        });

    const handleGenerateSummaryMail = () => {
        if (!phoneCall.dossier_phone_call_id) return;

        trackEvent((ampli, defaultProperties) => {
            ampli.generateRecapCallRmClicked({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });

        generateSummaryMail({
            dossierPhoneCallId: phoneCall.dossier_phone_call_id,
        });
    };

    return (
        <Box
            component="article"
            sx={{ backgroundColor: 'primary.dark' }}
            p={2}
            borderRadius={2}
            mb={3}
        >
            <Typography variant="body2" color="text.contrast" fontWeight={700} mb={2}>
                {formatMessage({
                    id: 'dossier.discussion.phone_call.recap_email_block.title',
                })}
            </Typography>
            <Button
                variant="contained"
                startIcon={
                    <AiIcon
                        width={14}
                        height={14}
                        style={{
                            animation: isGeneratingSummaryMail
                                ? 'spin 1s cubic-bezier(0.4, 0, 0.2, 1) 300ms infinite'
                                : 'none',
                            fill: 'currentcolor',
                        }}
                    />
                }
                size="small"
                sx={{
                    backgroundColor: TEMP_AI_BUTTON_BG,
                    color: 'text.success',
                    transition: 'opacity 0.2s ease',
                    '@keyframes spin': {
                        from: {
                            transform: 'rotate(0deg)',
                        },
                        to: {
                            transform: 'rotate(90deg)',
                        },
                    },
                    '&:hover,&:focus': {
                        opacity: '0.8',
                    },
                    '&:disabled': {
                        opacity: '0.5',
                        backgroundColor: TEMP_AI_BUTTON_BG,
                        color: 'text.success',
                    },
                }}
                disabled={!canGenerateEmailRecap || isGeneratingSummaryMail}
                onClick={handleGenerateSummaryMail}
            >
                {isGeneratingSummaryMail
                    ? formatMessage({
                          id: 'dossier.discussion.phone_call.recap_email_block.button_loading',
                      })
                    : formatMessage({
                          id: 'dossier.discussion.phone_call.recap_email_block.button_text',
                      })}
            </Button>
        </Box>
    );
};
